import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DialogProvider } from './components/AppDialog';
import { LoginPage, SignupPage, LinkedInCallbackPage } from './pages/AuthPages';
import { DashboardPage } from './pages/DashboardPage';
import { BuilderPage } from './pages/BuilderPage';
import { LandingPage, AtsCheckerPage, TermsOfUsePage, CookiePolicyPage } from './pages/OtherPages';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TemplateGalleryPage } from './pages/TemplatesPage';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { AtsGuidePage } from './pages/AtsGuidePage';
import { ResumeTipsPage } from './pages/ResumeTipsPage';
import { VideoInterviewPage } from './pages/VideoInterviewPage';
import { AIVideoInterviewPage } from './pages/AIVideoInterviewPage';
import { ContactPage } from './pages/ContactPage';
import { AboutPage } from './pages/AboutPage';
import { BlogPage, BlogPostPage } from './pages/BlogPage';
import { CookieBanner } from './components/CookieBanner';
import { ResumeImportModal } from './components/ResumeImportModal';
import { OnboardingModal } from './components/OnboardingModal';
import { GuestDraftSync } from './components/GuestDraftSync';
import { googleClientId, isGoogleOAuthConfigured } from './config/googleOAuth';
import { FullPageRocketLoader } from './components/RocketLoader';
import { trackPageView, bootstrapAnalytics } from './utils/analytics';

// Enable Firebase Analytics if the user already accepted analytics cookies.
bootstrapAnalytics();

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageRocketLoader message="Loading…" />;
  return user ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageRocketLoader />;
  return user ? <Navigate to="/dashboard" replace /> : children;
};

const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search]);

  return null;
};

// Logs a Firebase Analytics page_view on every SPA route change
const AnalyticsTracker = () => {
  const { pathname, search } = useLocation();

  React.useEffect(() => {
    trackPageView(pathname + search);
  }, [pathname, search]);

  return null;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/"               element={<LandingPage />} />
      <Route path="/templates"      element={<TemplateGalleryPage />} />
      <Route path="/ats-checker"    element={<AtsCheckerPage />} />
      <Route path="/pricing"        element={<PricingPage />} />
      <Route path="/how-it-works"   element={<HowItWorksPage />} />
      <Route path="/resume-tips"    element={<ResumeTipsPage />} />
      <Route path="/ats-guide"      element={<AtsGuidePage />} />
      <Route path="/terms-of-use"   element={<TermsOfUsePage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/cookie-policy"  element={<CookiePolicyPage />} />
      <Route path="/contact"        element={<ContactPage />} />
      <Route path="/about"          element={<AboutPage />} />
      <Route path="/blog"           element={<BlogPage />} />
      <Route path="/blog/:slug"     element={<BlogPostPage />} />
      <Route path="/login"          element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup"         element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/auth/linkedin/callback" element={<LinkedInCallbackPage />} />
      <Route path="/dashboard"      element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/builder/:type/:id" element={<PrivateRoute><BuilderPage /></PrivateRoute>} />
      <Route path="/build/:type"    element={<BuilderPage />} />
      <Route path="/ai-video-interview" element={<AIVideoInterviewPage />} />
      <Route path="/video-interview" element={<PrivateRoute><VideoInterviewPage /></PrivateRoute>} />
<Route path="*"               element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const app = (
    <BrowserRouter>
      <AuthProvider>
        <DialogProvider>
          <ScrollToTop />
          <AnalyticsTracker />
          <AppRoutes />
          <ResumeImportModal />
          <GuestDraftSync />
          <OnboardingModal />
          <CookieBanner />
        </DialogProvider>
      </AuthProvider>
    </BrowserRouter>
  );

  return isGoogleOAuthConfigured
    ? <GoogleOAuthProvider clientId={googleClientId}>{app}</GoogleOAuthProvider>
    : app;
}

export default App;
